import { useEffect, useState, useRef } from 'react'

const isKeyboardInput = (elem: HTMLElement) =>
  (elem.tagName === 'INPUT' &&
    !['button', 'submit', 'checkbox', 'file', 'image'].includes(
      (elem as HTMLInputElement).type,
    )) ||
  elem.hasAttribute('contenteditable')

const isSafariMobile = () => {
  const userAgent = navigator.userAgent
  return /iPad|iPhone|iPod/.test(userAgent) && /Safari/.test(userAgent) && !/Chrome|CriOS|FxiOS/.test(userAgent)
}

const useIsOnScreenKeyboardOpen = () => {
  const [isOpen, setOpen] = useState(false)
  const [keyboardHeight, setKeyboardHeight] = useState(0)
  const initialHeightRef = useRef<number>(0)
  const isKeyboardOpenRef = useRef<boolean>(false)
  
  useEffect(() => {
    // Only proceed if we're in Safari mobile
    if (!isSafariMobile()) {
      return
    }

    // Store the initial viewport height when component mounts
    initialHeightRef.current = window.innerHeight

    const handleFocusIn = (e: FocusEvent) => {
      if (!e.target) {
        return
      }
      const target = e.target as HTMLElement
      if (isKeyboardInput(target)) {
        isKeyboardOpenRef.current = true
        setOpen(true)
      }
    }
    document.addEventListener('focusin', handleFocusIn)
    
    const handleFocusOut = (e: FocusEvent) => {
      if (!e.target) {
        return
      }
      const target = e.target as HTMLElement
      if (isKeyboardInput(target)) {
        isKeyboardOpenRef.current = false
        setOpen(false)
        setKeyboardHeight(0)
      }
    }
    document.addEventListener('focusout', handleFocusOut)

    // Listen for viewport changes (keyboard show/hide)
    const handleResize = () => {
      const currentHeight = window.innerHeight
      const heightDifference = initialHeightRef.current - currentHeight
      
      // Only update if we have a significant height difference (keyboard is likely open)
      if (isKeyboardOpenRef.current && heightDifference > 150) {
        setKeyboardHeight(heightDifference)
      } else if (!isKeyboardOpenRef.current) {
        setKeyboardHeight(0)
      }
    }

    window.addEventListener('resize', handleResize)

    return () => {
      document.removeEventListener('focusin', handleFocusIn)
      document.removeEventListener('focusout', handleFocusOut)
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  // Only return true if we're in Safari mobile AND keyboard is open
  return {
    isOpen: isSafariMobile() ? isOpen : false,
    keyboardHeight: isSafariMobile() ? keyboardHeight : 0
  }
}

export default useIsOnScreenKeyboardOpen